/**
 * Lịch làm việc nhà máy + lập lịch theo năng lực máy — NGUỒN DUY NHẤT cho máy chủ (API, tRPC) và web.
 *
 * Quy tắc (giờ Việt Nam, mọi máy):
 *  - Ca chuẩn: 08:00–12:00 và 13:00–17:00, nghỉ Chủ Nhật.
 *  - Tăng ca 17:00–20:00 chỉ dùng khi cần để kịp hạn giao (allowOvertime = true).
 *  - Gối đầu x%: công đoạn sau được bắt đầu khi công đoạn trước đã chạy (100 − x)% (x lưu ở CÔNG ĐOẠN SAU).
 *
 * Máy chủ production chạy TZ=UTC: không dùng getHours/setHours/getDay (giờ tiến trình). VN cố định UTC+7,
 * không có giờ mùa hè, nên ngày VN được tính bằng phép cộng/trừ thời điểm thuần.
 */
import { vnDayOfWeek } from './vn-date';

const MINUTE_MS = 60_000;
const DAY_MS = 86_400_000;
const VN_OFFSET_MS = 7 * 3_600_000;

/** Mốc ca theo phút kể từ 00:00 giờ VN */
export const WORK_SHIFT = {
  START: 8 * 60,
  LUNCH_START: 12 * 60,
  LUNCH_END: 13 * 60,
  REGULAR_END: 17 * 60,
  OVERTIME_END: 20 * 60,
} as const;

export const WORK_SHIFT_DESCRIPTION =
  'Ca 08:00–12:00 và 13:00–17:00, nghỉ Chủ Nhật; tăng ca 17:00–20:00 chỉ khi cần để kịp hạn giao';

type Window = { start: number; end: number };

/** 00:00 giờ VN của ngày chứa thời điểm ms */
function vnDayStartMs(ms: number): number {
  return Math.floor((ms + VN_OFFSET_MS) / DAY_MS) * DAY_MS - VN_OFFSET_MS;
}

/** Các khung làm việc (thời điểm ms) của ngày VN bắt đầu lúc dayStart; Chủ Nhật không có khung nào */
function windowsOfDay(dayStart: number, allowOvertime: boolean): Window[] {
  if (vnDayOfWeek(new Date(dayStart)) === 0) return [];
  const end = allowOvertime ? WORK_SHIFT.OVERTIME_END : WORK_SHIFT.REGULAR_END;
  return [
    { start: dayStart + WORK_SHIFT.START * MINUTE_MS, end: dayStart + WORK_SHIFT.LUNCH_START * MINUTE_MS },
    { start: dayStart + WORK_SHIFT.LUNCH_END * MINUTE_MS, end: dayStart + end * MINUTE_MS },
  ];
}

const toMs = (d: Date | string | number): number => new Date(d).getTime();

/**
 * Thời điểm làm việc sớm nhất ≥ t: trước 08:00 → 08:00; nghỉ trưa → 13:00; sau giờ ca → 08:00 ngày làm việc kế tiếp;
 * Chủ Nhật → 08:00 thứ Hai. Đúng 12:00 / 17:00 (20:00 nếu tăng ca) coi là hết khung.
 */
export function nextWorkingInstant(t: Date | string | number, allowOvertime = true): Date {
  const ms = toMs(t);
  let day = vnDayStartMs(ms);
  for (let guard = 0; guard < 400; guard++, day += DAY_MS) {
    for (const w of windowsOfDay(day, allowOvertime)) {
      if (ms < w.end) return new Date(Math.max(ms, w.start));
    }
  }
  return new Date(ms);
}

/** Cộng `minutes` phút làm việc từ `start` (vắt qua nghỉ trưa / qua đêm / bỏ Chủ Nhật) */
export function addWorkingMinutes(start: Date | string | number, minutes: number, allowOvertime = true): Date {
  let cur = nextWorkingInstant(start, allowOvertime).getTime();
  let remaining = Math.max(0, Math.round(minutes)) * MINUTE_MS;
  if (remaining === 0) return new Date(cur);
  for (let guard = 0; guard < 5000; guard++) {
    const w = windowsOfDay(vnDayStartMs(cur), allowOvertime).find((x) => cur >= x.start && cur < x.end);
    if (!w) {
      cur = nextWorkingInstant(cur, allowOvertime).getTime();
      continue;
    }
    const avail = w.end - cur;
    if (remaining <= avail) return new Date(cur + remaining);
    remaining -= avail;
    cur = nextWorkingInstant(w.end, allowOvertime).getTime();
  }
  return new Date(cur);
}

/** Lùi `minutes` phút làm việc từ `end` về quá khứ (dùng neo hạn khuôn/kẽm trước giờ chạy máy) */
export function subtractWorkingMinutes(end: Date | string | number, minutes: number, allowOvertime = true): Date {
  let cur = toMs(end);
  let remaining = Math.max(0, Math.round(minutes)) * MINUTE_MS;
  if (remaining === 0) return new Date(cur);
  let day = vnDayStartMs(cur);
  for (let guard = 0; guard < 2000; guard++, day -= DAY_MS) {
    const windows = windowsOfDay(day, allowOvertime).reverse();
    for (const w of windows) {
      if (w.start >= cur) continue;
      const effEnd = Math.min(cur, w.end);
      const avail = effEnd - w.start;
      if (remaining <= avail) return new Date(effEnd - remaining);
      remaining -= avail;
      cur = w.start;
    }
  }
  return new Date(cur);
}

/** Số phút làm việc nằm trong [from, to) */
export function workingMinutesBetween(from: Date | string | number, to: Date | string | number, allowOvertime = true): number {
  const a = toMs(from);
  const b = toMs(to);
  if (!(b > a)) return 0;
  let total = 0;
  for (let day = vnDayStartMs(a), guard = 0; day < b && guard < 5000; day += DAY_MS, guard++) {
    for (const w of windowsOfDay(day, allowOvertime)) {
      const s = Math.max(a, w.start);
      const e = Math.min(b, w.end);
      if (e > s) total += e - s;
    }
  }
  return total / MINUTE_MS;
}

// ─────────────────────────────────────────────────────────────────────────────
// Gối đầu công đoạn (overlap) — một định nghĩa dùng chung
// ─────────────────────────────────────────────────────────────────────────────

export const OVERLAP_PERCENT_MIN = 0;
export const OVERLAP_PERCENT_MAX = 80;
/** Mặc định giữa các công đoạn liên tục (Cắt → In, Cán → Bế, Bế → Dán) */
export const DEFAULT_OVERLAP_PERCENT = 25;
/** KCS → Đóng gói */
export const KCS_TO_PACKING_OVERLAP_PERCENT = 50;

export function clampOverlapPercent(value: unknown): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.min(OVERLAP_PERCENT_MAX, Math.max(OVERLAP_PERCENT_MIN, n));
}

/** % sản lượng công đoạn trước phải đạt trước khi công đoạn sau được bắt đầu: gối đầu 25% → 75% */
export function overlapReleasePercent(overlapPercent: unknown): number {
  return 100 - clampOverlapPercent(overlapPercent);
}

/**
 * Số phút làm việc tính từ lúc công đoạn TRƯỚC bắt đầu đến lúc công đoạn SAU được phép bắt đầu:
 * chuẩn bị (setup) của công đoạn trước + (100 − x)% thời gian chạy của công đoạn trước; x là gối đầu của công đoạn sau.
 */
export function overlapLeadMinutes(prevSetupMinutes: number, prevRunMinutes: number, overlapPercent: unknown): number {
  const release = overlapReleasePercent(overlapPercent) / 100;
  return Math.max(0, Math.round(Number(prevSetupMinutes) || 0)) + Math.ceil(Math.max(0, Number(prevRunMinutes) || 0) * release);
}

/** Câu mô tả dùng chung trên giao diện */
export function describeOverlap(overlapPercent: unknown): string {
  const x = clampOverlapPercent(overlapPercent);
  if (x === 0) return 'Không gối đầu: công đoạn sau bắt đầu khi công đoạn trước xong 100%.';
  return `Gối đầu ${x}%: công đoạn sau được bắt đầu khi công đoạn trước đã chạy ${100 - x}% sản lượng.`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Tìm khe trống sớm nhất trên một máy
// ─────────────────────────────────────────────────────────────────────────────

export interface BookedInterval {
  start: Date | string | number;
  end: Date | string | number;
  /** id công đoạn đang chiếm khoảng này (để báo trùng) */
  stepId?: string;
  [key: string]: unknown;
}

export interface SlotResult {
  start: Date;
  end: Date;
}

/** Hai khoảng [aStart, aEnd) và [bStart, bEnd) giao nhau */
export function intervalsOverlap(
  aStart: Date | string | number,
  aEnd: Date | string | number,
  bStart: Date | string | number,
  bEnd: Date | string | number,
): boolean {
  return toMs(aStart) < toMs(bEnd) && toMs(bStart) < toMs(aEnd);
}

/**
 * Khe trống sớm nhất trên một máy: bắt đầu ≥ earliestStart, trong giờ ca, đủ `durationMinutes` phút làm việc
 * (công việc được vắt qua nghỉ trưa / qua đêm như lịch ca) và KHÔNG giao với khoảng bận nào. Không chia nhỏ công việc.
 */
export function findEarliestSlot(params: {
  busy: BookedInterval[];
  earliestStart: Date | string | number;
  durationMinutes: number;
  allowOvertime?: boolean;
}): SlotResult {
  const allowOvertime = params.allowOvertime ?? false;
  const busy = params.busy
    .map((b) => ({ s: toMs(b.start), e: toMs(b.end) }))
    .filter((b) => Number.isFinite(b.s) && Number.isFinite(b.e) && b.e > b.s)
    .sort((x, y) => x.s - y.s || x.e - y.e);
  let t = toMs(params.earliestStart);
  for (let guard = 0; guard < 10000; guard++) {
    const start = nextWorkingInstant(t, allowOvertime);
    const end = addWorkingMinutes(start, params.durationMinutes, allowOvertime);
    const s = start.getTime();
    const e = end.getTime();
    const hit = busy.find((b) => b.s < Math.max(e, s + 1) && b.e > s);
    if (!hit) return { start, end };
    t = Math.max(hit.e, s + MINUTE_MS);
  }
  const start = nextWorkingInstant(t, allowOvertime);
  return { start, end: addWorkingMinutes(start, params.durationMinutes, allowOvertime) };
}

/**
 * Thứ tự lấy khóa máy (advisory lock): duy nhất, bỏ rỗng, sắp tăng dần — mọi người lập lịch khóa cùng một thứ tự
 * nên không thể khóa chéo (deadlock).
 */
export function machineLockOrder(machineIds: Array<string | null | undefined>): string[] {
  return [...new Set(machineIds.filter((x): x is string => typeof x === 'string' && x.length > 0))].sort((a, b) =>
    a < b ? -1 : a > b ? 1 : 0,
  );
}
